import { useState } from 'react';
import type { ReactElement } from 'react';
import { paisa } from '@fleet/shared';
import { useVehiclesQuery } from '@/api/endpoints';
import type { Availability, Vehicle } from '@/api/types';
import { useCan } from '@/app/session';
import { LinkButton } from '@/design/Button';
import { DataTable } from '@/design/DataTable';
import type { Column } from '@/design/DataTable';
import { Identifier, MoneyText } from '@/design/Data';
import { Icon } from '@/design/icons';
import { Panel } from '@/design/Panel';
import { StatusPill } from '@/design/StatusPill';
import { AVAILABILITY, CATEGORY_LABELS } from './labels';

/**
 * The fleet.
 *
 * Plate first, in monospace, because that is how every conversation about a
 * vehicle starts. Then what it is, then whether it can go out today. The
 * website column is last: it matters to the owner, not to the counter.
 */
const FILTERS = [
  { value: '', label: 'All' },
  { value: 'available', label: 'Available now' },
  { value: 'unavailable', label: 'Not available' },
] as const;

export function VehiclesPage(): ReactElement {
  const [availability, setAvailability] = useState<string>('');
  const { data, isLoading, isError, refetch } = useVehiclesQuery({ availability });
  const canManage = useCan('settings.manage');

  const columns: readonly Column<Vehicle>[] = [
    {
      key: 'plate',
      header: 'Plate',
      render: (vehicle) => <Identifier>{vehicle.plate}</Identifier>,
    },
    {
      key: 'vehicle',
      header: 'Vehicle',
      render: (vehicle) => (
        <span>
          {vehicle.display_name}
          {vehicle.year !== null && vehicle.year !== undefined && (
            <span className="tnum ml-1.5 text-ink-muted">{vehicle.year}</span>
          )}
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      secondary: true,
      render: (vehicle) => CATEGORY_LABELS[vehicle.category],
    },
    { key: 'seats', header: 'Seats', numeric: true, secondary: true, render: (v) => v.seats },
    {
      // The server's answer, not the row's.
      //
      // This column read `vehicle.status`, a field somebody has to remember to
      // change — so a Celerio that went out on a booking still said
      // "Available" to whoever was quoting the next customer. It now comes
      // from the bookings, where the truth already was.
      key: 'status',
      header: 'Status',
      render: (vehicle) => (
        <StatusPill
          status={
            AVAILABILITY[(vehicle.availability ?? 'available') as Availability] ??
            AVAILABILITY.available
          }
        />
      ),
    },
    {
      key: 'range',
      header: 'Goes',
      secondary: true,
      render: (vehicle) =>
        vehicle.travel_range === 'inside_only' ? (
          <span className="text-attention">Valley only</span>
        ) : (
          <span className="text-ink-muted">Anywhere</span>
        ),
    },
    {
      key: 'odometer',
      header: 'Odometer',
      numeric: true,
      secondary: true,
      render: (vehicle) => (
        <span className="identifier">{(vehicle.odometer ?? 0).toLocaleString('en-GB')}</span>
      ),
    },
    {
      key: 'rate',
      header: 'From',
      numeric: true,
      secondary: true,
      render: (vehicle) => <MoneyText amount={paisa(vehicle.indicative_rate ?? 0)} />,
    },
  ];
  // The Website column is gone, at the client's request. It published a vehicle
  // to a public site we no longer build for clients — the feed that served it
  // was retired when the product became multi-tenant — so the button changed a
  // flag nothing reads and the column reported on nothing. The field and the
  // endpoint are still there, unused, in case a public fleet page comes back.

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-display font-semibold">Vehicles</h1>
        {canManage && (
          <LinkButton to="/fleet/new" intent="primary" size="touch">
            <Icon name="plus" width="16" height="16" />
            Add vehicle
          </LinkButton>
        )}
      </div>

      <Panel
        title="The fleet"
        // No count while the load is failing: a panel that says "1 vehicle"
        // above an error message is the screen contradicting itself.
        // Grouped by what each row says — out with a customer first, then what
        // can go out now — so the answer to "what can I give somebody" is at
        // the top rather than scattered down an alphabetical list.
        note={
          data === undefined || isError
            ? 'Grouped by status: out now, then free, then off the road'
            : `${String(data.count)} ${data.count === 1 ? 'vehicle' : 'vehicles'}, grouped by status`
        }
        current
      >
        {/* "What can I give somebody right now" is the question this screen
            gets asked, and it is not any single status value: a vehicle can
            be free on its row and out on a trip. */}
        <div className="flex flex-wrap gap-1 border-b border-rule px-4 py-2">
          {FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setAvailability(option.value)}
              className={[
                'rounded-sm px-2.5 py-1 text-base',
                availability === option.value
                  ? 'bg-signal-wash font-semibold text-signal'
                  : 'text-ink-muted hover:bg-paper hover:text-ink',
              ].join(' ')}
            >
              {option.label}
            </button>
          ))}
        </div>

        <DataTable
          columns={columns}
          rows={data?.results ?? []}
          rowKey={(vehicle) => vehicle.id}
          rowHref={canManage ? (vehicle) => `/fleet/${vehicle.id}` : undefined}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          errorHeadline="The fleet did not load"
          errorDetail="The server did not answer. Check the connection and load it again."
          empty={{
            headline: 'No vehicles yet',
            detail:
              'Add a vehicle with its plate, category and seats. It is bookable straight away.',
            action: canManage ? (
              <LinkButton to="/fleet/new" intent="primary">
                <Icon name="plus" width="16" height="16" />
                Add vehicle
              </LinkButton>
            ) : undefined,
          }}
        />
      </Panel>
    </div>
  );
}
